// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  addDiscount,
  getCart,
  selectCartIsActive,
  selectCartTotal,
  selectCartUUID,
} from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { FetchResponse } from '@gandi/resource/redux';
import { useTranslator } from '@gandi/translate';

import { Base } from '@gandi/ui';
import { ListRow, ListItem, TitleCell, Action } from '@gandi/ui/deprecated';

import { GET } from 'gandi.redux-fetcher';

import { useAppSelector } from 'src/hooks';

import type { Dispatch, GetState } from '../../../types';

import Coupon from './Coupon';
import Product from './Product';
import ReferAFriendOffer from './ReferAFriendOffer';
import {
  REFER_A_FRIEND_REASON,
  formatCoupon,
  formatSuggestion,
} from './helpers';
import type {
  CartCouponSuggestion,
  CartProductSuggestion,
  CartSuggestReason,
} from './types';

const SHS_DISCOUNT_CODE = 'SHS50';

function useTitle(reason: CartSuggestReason): string {
  const { __ } = useTranslator();
  let title = __('This may interest you too');

  // eslint-disable-next-line default-case
  switch (reason) {
    case 'at_least_one_domain_and_first_shs':
    case 'at_least_one_domain_and_no_shs':
    case 'refer_a_friend_shs_offer':
      title = __('Host your websites and web applications');
  }

  return title;
}

const fetchCartSuggest = () => (dispatch: Dispatch, getState: GetState) => {
  const cartId = selectCartUUID(getState());

  return dispatch(
    GET('/cartsuggest/suggest', {
      // $FlowFixMe[prop-missing]
      params: {
        cart: cartId,
      },
      // $FlowFixMe[incompatible-exact]
      on2XX: (response: FetchResponse<>) => response,
      // $FlowFixMe[incompatible-exact]
      not2XX: (response: FetchResponse<>) => Promise.reject(response),
    }),
  );
};

const getSessionStorageKey = (sharingId: string) =>
  `cartsuggest-dismissed-${sharingId}`;

export function CartSuggest(): null | React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const sharingId = useAppSelector(selectCurrentId);
  const cartActive = useAppSelector(selectCartIsActive);
  const cartTotal = useAppSelector(selectCartTotal);

  const [suggestions, setSuggestions] = React.useState<
    Array<CartProductSuggestion>,
  >([]);
  const [coupons, setCoupons] = React.useState<Array<CartCouponSuggestion>>([]);
  const [dismissed, setDismissed] = React.useState(
    !global.window ||
      !global.window.sessionStorage ||
      !!global.window.sessionStorage.getItem(getSessionStorageKey(sharingId)),
  );

  const title = useTitle([...suggestions, ...coupons][0].reason);

  // fetchIfNeeded
  React.useEffect(() => {
    async function fetch() {
      try {
        const response = await dispatch(fetchCartSuggest());

        if (response.status === 204) {
          return;
        }

        if (!Array.isArray(response.data) || !response.data.length) {
          return;
        }

        const { data } = response;

        const [formattedSuggestions, formattedCoupons] = [
          ['suggest', formatSuggestion],
          ['coupon', formatCoupon],
        ].map(([type, format]) =>
          data.filter(sugg => sugg.type === type).map(format),
        );

        await Promise.allSettled(
          coupons.map(({ discount: { code, related_product } }) =>
            // $FlowFixMe[incompatible-call]
            dispatch(addDiscount(code, related_product)),
          ),
        );
        await dispatch(getCart());

        setSuggestions(formattedSuggestions);
        setCoupons(formattedCoupons);
      } catch (err) {
        // pass
      }
    }

    if (sharingId && cartActive && !dismissed) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    dismissed ||
    !sharingId ||
    !cartActive ||
    (!suggestions.length && !coupons.length)
  ) {
    return null;
  }

  const handleClickOnDismiss: (evt: SyntheticMouseEvent<>) => void = (
    evt: SyntheticMouseEvent<>,
  ) => {
    evt.preventDefault();

    if (global.window.sessionStorage) {
      try {
        global.window.sessionStorage.setItem(
          getSessionStorageKey(sharingId),
          true,
        );
      } catch (err) {
        // Unable to save setting in session storage, pass.
        // May happen due to storage restrictions (over-quota, etc...)
      }
    }

    setDismissed(true);
  };
  const dismissLabel = __('Dismiss this suggestion');

  // Specific display of the SHS 6months-free Refer-A-Friend offer.
  const offer = suggestions.find(
    ({ reason }) => reason === REFER_A_FRIEND_REASON,
  );

  const cartHasSHSDiscountApplied = (cartTotal.discounts || []).find(
    discount => discount.code === SHS_DISCOUNT_CODE,
  );

  if (offer && !cartHasSHSDiscountApplied) {
    return (
      <ReferAFriendOffer
        offer={offer}
        onDismissClick={handleClickOnDismiss}
        dismissLabel={dismissLabel}
      />
    );
  }

  return (
    <Base marginBottom="base">
      <ListRow separatorBottom>
        <ListItem>
          <TitleCell>{title}</TitleCell>
        </ListItem>
        <Action icon="cross" theme="danger" onClick={handleClickOnDismiss}>
          {dismissLabel}
        </Action>
      </ListRow>
      {coupons.map((coupon, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Coupon key={`coupon-${idx}`} coupon={coupon} />
      ))}
      {suggestions.map((suggestion, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Product key={`suggestion-${idx}`} suggestion={suggestion} />
      ))}
    </Base>
  );
}
